import { useEffect, useState } from 'react';
import { session } from '@/lib/session';
import { enrollPasskey, listPasskeys, removePasskey, passkeySupported } from '@/lib/webauthn';
import api from '@/lib/api';

const fmtDt = (d) => {
  if (!d) return 'N/A';
  try {
    return new Date(d).toLocaleString('en-US', {
      year: 'numeric', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit'
    });
  } catch { return d; }
};

function PasskeySection() {
  const [creds, setCreds] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [enrolling, setEnrolling] = useState(false);
  const [passkeyName, setPasskeyName] = useState('');

  const load = () => {
    setLoading(true);
    listPasskeys()
      .then(setCreds)
      .catch((e) => setError(e.response?.data?.detail || e.message))
      .finally(() => setLoading(false));
  };

  useEffect(() => { load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const handleEnroll = async () => {
    setEnrolling(true);
    setError('');
    try {
      await enrollPasskey(passkeyName.trim() || 'Passkey');
      setPasskeyName('');
      load();
    } catch (e) {
      if (e?.name === 'NotAllowedError') {
        setError('Passkey creation was cancelled or timed out.');
      } else {
        setError(e.response?.data?.detail || e.message || 'Could not create passkey.');
      }
    } finally {
      setEnrolling(false);
    }
  };

  const handleRemove = async (id) => {
    setError('');
    try {
      await removePasskey(id);
      load();
    } catch (e) {
      setError(e.response?.data?.detail || e.message);
    }
  };

  return (
    <div data-testid="mfa-passkey-section" className="mb-10">
      <h2 className="font-heading font-semibold text-black mb-1" style={{ fontSize: '18px' }}>
        Passkeys (MFA)
      </h2>
      <p className="font-body text-gray-500 mb-5" style={{ fontSize: '14px', lineHeight: 1.5, maxWidth: '560px' }}>
        Once you enroll a passkey, every future sign-in for this account requires it —
        password alone will no longer grant access.
      </p>

      {!passkeySupported() && (
        <p className="font-body text-gray-500 mb-4" style={{ fontSize: '13px' }} data-testid="mfa-unsupported-note">
          This browser does not support passkeys.
        </p>
      )}

      {error && (
        <div style={{ background: '#fee2e2', borderRadius: '3px', padding: '10px 14px', marginBottom: '16px' }} data-testid="mfa-section-error">
          <p style={{ fontSize: '13px', color: '#b91c1c' }}>{error}</p>
        </div>
      )}

      {loading ? (
        <p className="font-body text-gray-400" style={{ fontSize: '14px' }}>Loading passkeys…</p>
      ) : creds.length === 0 ? (
        <p className="font-body text-gray-400 mb-5" style={{ fontSize: '14px' }} data-testid="mfa-no-passkeys">
          No passkeys enrolled yet.
        </p>
      ) : (
        <div className="mb-5" data-testid="mfa-passkey-list">
          {creds.map((c) => (
            <div
              key={c.id}
              className="flex items-center justify-between px-4 py-3 mb-2"
              style={{ border: '1px solid #e5e5e5', borderRadius: '3px' }}
              data-testid={`mfa-passkey-row-${c.id}`}
            >
              <div>
                <p className="font-body text-black" style={{ fontSize: '14px', fontWeight: 500 }}>{c.name}</p>
                <p className="font-body text-gray-400" style={{ fontSize: '12px' }}>
                  Added {fmtDt(c.created_at)} · Credential {c.credential_id_display}
                </p>
              </div>
              <button
                onClick={() => handleRemove(c.id)}
                className="font-body"
                style={{ fontSize: '13px', color: '#b91c1c', background: 'none', border: 'none', cursor: 'pointer' }}
                data-testid={`mfa-remove-btn-${c.id}`}
              >
                Remove
              </button>
            </div>
          ))}
        </div>
      )}

      <div className="flex gap-3 items-center flex-wrap">
        <input
          type="text"
          placeholder="Passkey name (e.g. MacBook Touch ID)"
          value={passkeyName}
          onChange={(e) => setPasskeyName(e.target.value)}
          style={{
            height: '40px', border: '1px solid #d9dee6', borderRadius: '3px', padding: '0 12px',
            fontSize: '14px', minWidth: '260px',
          }}
          data-testid="mfa-passkey-name-input"
        />
        <button
          onClick={handleEnroll}
          disabled={enrolling || !passkeySupported()}
          className="pub-btn-primary"
          style={{ fontSize: '14px', minHeight: '40px', opacity: enrolling ? 0.6 : 1 }}
          data-testid="mfa-enroll-btn"
        >
          {enrolling ? 'Creating…' : 'Add a passkey'}
        </button>
      </div>
    </div>
  );
}

function IncidentSection() {
  const [incidents, setIncidents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [restoring, setRestoring] = useState(null);
  const [reason, setReason] = useState('');

  const load = () => {
    setLoading(true);
    api.get('/security/incidents')
      .then((r) => setIncidents(r.data.incidents))
      .catch((e) => setError(e.response?.data?.detail || e.message))
      .finally(() => setLoading(false));
  };

  useEffect(() => { load(); }, []);

  const handleRestore = async (incidentId) => {
    setError('');
    try {
      await api.post(`/security/incidents/${incidentId}/restore`, {
        classification: 'FALSE_POSITIVE',
        reason: reason.trim() || 'Reviewed and restored by security operator.',
      });
      setRestoring(null);
      setReason('');
      load();
    } catch (e) {
      setError(e.response?.data?.detail || e.message);
    }
  };

  return (
    <div data-testid="security-incident-section">
      <h2 className="font-heading font-semibold text-black mb-1" style={{ fontSize: '18px' }}>
        Security Incidents
      </h2>
      <p className="font-body text-gray-500 mb-5" style={{ fontSize: '14px', lineHeight: 1.5, maxWidth: '560px' }}>
        Restoring an account requires a fresh passkey verification within the last 15 minutes.
      </p>

      {error && (
        <div style={{ background: '#fee2e2', borderRadius: '3px', padding: '10px 14px', marginBottom: '16px' }} data-testid="incident-section-error">
          <p style={{ fontSize: '13px', color: '#b91c1c' }}>{error}</p>
        </div>
      )}

      {loading ? (
        <p className="font-body text-gray-400" style={{ fontSize: '14px' }}>Loading incidents…</p>
      ) : incidents.length === 0 ? (
        <p className="font-body text-gray-400" style={{ fontSize: '14px' }} data-testid="no-incidents">
          No security incidents recorded.
        </p>
      ) : (
        incidents.map((inc) => (
          <div
            key={inc.id}
            className="px-4 py-3 mb-2"
            style={{ border: '1px solid #e5e5e5', borderRadius: '3px' }}
            data-testid={`incident-row-${inc.id}`}
          >
            <div className="flex items-center justify-between">
              <div>
                <p className="font-body text-black" style={{ fontSize: '14px', fontWeight: 500 }}>
                  Incident {inc.id.slice(0, 8)}… — {inc.status}
                </p>
                <p className="font-body text-gray-400" style={{ fontSize: '12px' }}>
                  Opened {fmtDt(inc.opened_at)} · {inc.event_ids?.length || 0} event(s)
                  {inc.classification ? ` · ${inc.classification}` : ''}
                </p>
              </div>
              {inc.status === 'OPEN' && (
                restoring === inc.id ? (
                  <div className="flex gap-2 items-center">
                    <input
                      type="text"
                      placeholder="Restoration reason"
                      value={reason}
                      onChange={(e) => setReason(e.target.value)}
                      style={{ height: '34px', border: '1px solid #d9dee6', borderRadius: '3px', padding: '0 10px', fontSize: '13px' }}
                      data-testid={`incident-reason-input-${inc.id}`}
                    />
                    <button
                      onClick={() => handleRestore(inc.id)}
                      className="pub-btn-primary"
                      style={{ fontSize: '13px', minHeight: '34px' }}
                      data-testid={`incident-confirm-restore-btn-${inc.id}`}
                    >
                      Confirm restore
                    </button>
                  </div>
                ) : (
                  <button
                    onClick={() => setRestoring(inc.id)}
                    className="pub-btn-primary"
                    style={{ fontSize: '13px', minHeight: '34px' }}
                    data-testid={`incident-restore-btn-${inc.id}`}
                  >
                    Restore account
                  </button>
                )
              )}
            </div>
          </div>
        ))
      )}
    </div>
  );
}

export default function Security() {
  const user = session.getUser();
  const isSecurityOperator = user?.role === 'SECURITY_OPERATOR';

  return (
    <div data-testid="security-page">
      <h1 className="page-heading mb-1">Security</h1>
      <p className="font-body text-gray-500 mb-8" style={{ fontSize: '15px', lineHeight: 1.5 }}>
        Manage passkey multi-factor authentication for this privileged account.
      </p>

      <PasskeySection />
      {isSecurityOperator && <IncidentSection />}
    </div>
  );
}
